import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Mail, Ruler, Pencil } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { api, errMsg, fmtDate, money } from "@/lib/api";
import { qtyText } from "./Leads";
import { emailStatus, TONE_CLASS } from "@/lib/emailStatus";

const range = (lo: number, hi: number, mid: number) => (lo === hi ? money(mid) : `${money(lo)} – ${money(hi)}`);

function Override({ lead, onDone }: any) {
  const [amount, setAmount] = useState("");
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const apply = async (clear = false) => {
    setBusy(true);
    try {
      await api.post(`/orgs/me/field-quotes/${lead.id}/override`, { amount: clear ? null : amount, reason });
      toast.success(clear ? "Override removed" : "Adjusted price saved"); setAmount(""); setReason(""); onDone();
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  return (
    <div className="border-t pt-4 space-y-2" data-testid="fq-override-panel">
      <Label className="text-xs">Adjust final price (the calculated price is always kept)</Label>
      <Input className="h-12 text-base font-mono" inputMode="decimal" placeholder="$ adjusted price" value={amount} onChange={(e) => setAmount(e.target.value)} data-testid="fq-override-amount" />
      <Input className="h-12 text-base" placeholder="Reason (required), e.g. steep slope, heavy staining" value={reason} onChange={(e) => setReason(e.target.value)} data-testid="fq-override-reason" />
      <div className="flex gap-2">
        <Button variant="outline" className="h-12 flex-1" disabled={busy || !amount || reason.trim().length < 3} onClick={() => apply()} data-testid="fq-override-save">Apply adjusted price</Button>
        {lead.price_override && <Button variant="ghost" className="h-12" disabled={busy || reason.trim().length < 3} onClick={() => apply(true)} data-testid="fq-override-clear">Remove</Button>}
      </div>
    </div>
  );
}

const STATUS_TEXT: Record<string, [string, string]> = {
  accepted: ["Accepted by the email provider — delivery not yet confirmed", "text-primary"],
  queued: ["Queued — it will retry automatically", "text-amber-700"],
  failed: ["Sending failed", "text-destructive"],
  backup: ["Sent via backup service — no delivery tracking", "text-muted-foreground"],
  suppressed: ["Not sent: this address previously bounced or reported spam", "text-destructive"],
};

export function ReviewPanel({ d, onRemeasure, onEditDetails, onReload }: any) {
  const { lead, quote, emails } = d;
  const [sending, setSending] = useState(false);
  const [result, setResult] = useState<any>(null);
  const ov = lead.price_override;
  const send = async () => {
    setSending(true); setResult(null);
    try { const { data } = await api.post(`/orgs/me/field-quotes/${lead.id}/send`, { idempotency_key: crypto.randomUUID() }); setResult(data); onReload(); }
    catch (e) { toast.error(errMsg(e)); } finally { setSending(false); }
  };
  const sent = emails.filter((e: any) => e.template === "field_estimate");
  useEffect(() => {
    if (!result) { return undefined; }
    // Delivery confirmations arrive by webhook a few seconds after acceptance; refresh briefly so the label updates.
    let n = 0;
    const t = setInterval(() => { n += 1; onReload(); if (n >= 12) { clearInterval(t); } }, 5000);
    return () => { clearInterval(t); };
  }, [result]); // eslint-disable-line react-hooks/exhaustive-deps
  return (
    <div className="space-y-5" data-testid="fq-review-step">
      <div className="bg-card border rounded-xl p-5 space-y-3">
        <p className="text-xs uppercase tracking-wider text-muted-foreground">{lead.service_label}</p>
        <p className="text-sm">{lead.confirmed_service_address}</p>
        <div className="flex items-baseline justify-between gap-3">
          <span className="text-sm text-muted-foreground">Measured (approx.)</span>
          <span className="font-mono text-xl font-bold" data-testid="fq-quantity">{qtyText(lead)}</span>
        </div>
        <div className="flex items-baseline justify-between gap-3">
          <span className="text-sm text-muted-foreground">Calculated estimate</span>
          <span className={`font-mono ${ov ? "line-through text-muted-foreground" : "text-2xl font-bold"}`} data-testid="fq-calculated-price">{range(quote.low_cents, quote.high_cents, quote.original_amount_cents)}</span>
        </div>
        {ov && (
          <div className="rounded-lg bg-amber-50 border border-amber-200 p-3" data-testid="fq-adjusted-price-box">
            <div className="flex items-baseline justify-between"><span className="text-sm font-medium text-amber-900">Adjusted price</span><span className="font-mono text-2xl font-bold" data-testid="fq-adjusted-price">{money(ov.cents)}</span></div>
            <p className="text-xs text-amber-900 mt-1">Reason: {ov.reason} · {fmtDate(ov.at)}</p>
          </div>
        )}
        {quote.line_items?.map((li: any, i: number) => <div key={i} className="flex justify-between text-xs text-muted-foreground"><span>{li.label}</span><span>{money(li.cents)}</span></div>)}
        <Override lead={lead} onDone={onReload} />
      </div>
      <div className="grid grid-cols-2 gap-2">
        <Button variant="outline" className="h-12" onClick={onRemeasure} data-testid="fq-remeasure-button"><Ruler className="h-4 w-4 mr-1" />Re-measure</Button>
        <Button variant="outline" className="h-12" onClick={onEditDetails} data-testid="fq-edit-details-button"><Pencil className="h-4 w-4 mr-1" />Edit details</Button>
      </div>
      <div className="bg-card border rounded-xl p-5 space-y-3" data-testid="fq-send-panel">
        <Button size="lg" className="w-full h-14 text-base" onClick={send} disabled={sending || !lead.email} data-testid="fq-send-button">
          <Mail className="h-5 w-5 mr-2" />{sending ? "Sending…" : sent.length ? "Resend estimate" : "Send estimate to homeowner"}
        </Button>
        {!lead.email && <p className="text-xs text-muted-foreground">Add the homeowner’s email (Edit details) to send the estimate.</p>}
        {result && (() => {
          const job = sent.find((e: any) => e.id === result.job_id && e.status === "sent" && e.route === "resend");
          if (job) { const s = emailStatus(job); return <p className={`text-sm font-medium ${TONE_CLASS[s.tone]}`} data-testid="fq-send-status" data-status={job.delivery_status}>{s.label}</p>; }
          return <p className={`text-sm font-medium ${STATUS_TEXT[result.status][1]}`} data-testid="fq-send-status" data-status={result.status}>{STATUS_TEXT[result.status][0]}{result.detail ? ` (${result.detail})` : ""}</p>;
        })()}
        {sent.length > 0 && <ul className="text-xs text-muted-foreground space-y-0.5" data-testid="fq-send-history">{sent.map((e: any) => { const s = emailStatus(e); return <li key={e.id}>{fmtDate(e.created_at)} · <span className={TONE_CLASS[s.tone]} data-testid="fq-send-history-status">{s.label}</span>{e.last_error ? ` (${e.last_error})` : ""}</li>; })}</ul>}
      </div>
      {lead.price_history?.length > 0 && (
        <div className="bg-card border rounded-xl p-5" data-testid="fq-price-history">
          <p className="font-semibold mb-2 text-sm">Price edit history</p>
          <ul className="text-xs space-y-1.5">
            {lead.price_history.map((h: any, i: number) => (
              <li key={i}>{fmtDate(h.at)} · {h.override_cents ? `Adjusted to ${money(h.override_cents)}` : "Adjustment removed"}{h.original_estimate_cents != null ? ` (calculated ${range(h.original_low_cents, h.original_high_cents, h.original_estimate_cents)})` : ""} — {h.reason} · {d.users?.[h.by_user_id] || "team member"}</li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}
